import { Injectable, EventEmitter } from "@angular/core"
import { throwError, Observable, of, Subscription } from "rxjs"
import { catchError, map } from "rxjs/operators"
import { HttpHeaders, HttpClient } from "@angular/common/http"
import { Router } from "@angular/router"
import { environment } from "src/environments/environment"
import { ServiceLocator } from "./locator.service"
import { ToastrService, ToastrConfig } from "ngx-toastr"
import { TranslateService } from "@ngx-translate/core"

declare var $: any

@Injectable()
export class HelpersService {
	// Storage
	private storageAccessTokenKey = "of-storage-access-token"
	private storageRefreshTokenKey = "of-storage-refresh-token"
	private storageExpiresIn = "of-storage-expires-in"
	private storageIsLogged = "of-storage-is-logged"
	private storageUserInfo = "of-storage-user-info"
	private storageUserData = "of-storage-user-data"
	private storageLanguage = "of-storage-lang"

	// Events
	public notifications: EventEmitter<any> = new EventEmitter()
	private polling$: Subscription

	public DEFAULT_LANG = "en"
	public pathImgsMarkers = environment.path_imgs_markers
	public toastrOptions = {
		closeButton: true,
		newestOnTop: true,
		progressBar: false,
		positionClass: "toast-bottom-center",
		preventDuplicates: false,
		timeOut: 4000,
		extendedTimeOut: 1000
	}
	protected translate: TranslateService
	public toastr: ToastrService

	constructor(
		protected http: HttpClient,
		protected router: Router
	) {
		this.toastr = ServiceLocator.injector.get(ToastrService)
		this.translate = ServiceLocator.injector.get(TranslateService)
	}

	// User info

	get userInfo(): any {
		const user = localStorage.getItem(this.storageUserInfo)
		return user && user !== "undefined" ? JSON.parse(user) : null
	}

	get userData(): any {
		const user = localStorage.getItem(this.storageUserData)
		return user && user !== "undefined" ? JSON.parse(user) : null
	}

	public getUserData(): Observable<any> {
		const url = environment.DOMAIN_URL + "/api2/marketing/user/me"
		return this.http.get(url, this.getHTTPHeader(true)).pipe(
			map(data => {
				localStorage.setItem(this.storageUserData, JSON.stringify(data))
				return data
			}),
			catchError(error => {
				return this.handleError(error, "getUserData")
			})
		)
	}

	get isAdmin(): any {
		if (this.userInfo) {
			const userRole = this.userInfo.filter(roles => roles === "ROLE_ADMIN")
			if (userRole.length) {
				return userRole
			}
		}
		return false
	}

	get isOpenFiberUser() {
		if (environment.show_massive_company_project) {
			return true
		} else if (this.userData && this.userData.company) {
			return this.userData.company.toLowerCase().replace(/\s/g, "") === "openfiber"
		} else {
			return false
		}
	}

	get isFastwebUser() {
		if (environment.show_massive_company_project) {
			return true
		} else if (this.userData && this.userData.company) {
			return (
				this.userData.company.toLowerCase() === "fastweb" ||
				this.userData.company.toLowerCase().includes("fastweb")
			)
		} else {
			return false
		}
	}

	get isWindTreUser() {
		if (environment.show_massive_company_project) {
			return true
		} else if (this.userData && this.userData.company) {
			return (
				this.userData.company.toLowerCase() === "wind" ||
				this.userData.company.toLowerCase() === "wind tre spa" ||
				this.userData.company.toLowerCase() === "windtre"
			)
		} else {
			return false
		}
	}

	get accessToken(): string {
		return localStorage.getItem(this.storageAccessTokenKey)
	}

	public clear() {
		localStorage.removeItem(this.storageAccessTokenKey)
		localStorage.removeItem(this.storageRefreshTokenKey)
		localStorage.removeItem(this.storageExpiresIn)
		localStorage.removeItem(this.storageIsLogged)
		localStorage.removeItem(this.storageUserInfo)
		localStorage.removeItem(this.storageUserData)
		localStorage.removeItem(this.storageLanguage)
	}

	// Api

	public getFiles(): Observable<any> {
		return this.http.get<any>(environment.DOMAIN_URL + "/api2/marketing/massivep2p", this.getHTTPHeader(true)).pipe(
			map(data => {
				// return FilesMock;
				return data
			}),
			catchError(error => {
				return this.handleError(error, "getFiles")
			})
		)
	}

	public downloadZip(id) {
		const url = environment.DOMAIN_URL + "/api2/marketing/massivep2p/" + id
		const headers = this.getHTTPHeader(true)
		headers["responseType"] = "blob"
		return this.http.get(url, headers) // check se usare blob o arrayBuffer
	}

	public getNotifications(): Observable<any> {
		return this.http
			.get<any>(environment.DOMAIN_URL + "/api2/marketing/notification/my/all", this.getHTTPHeader(true))
			.pipe(
				map(data => {
					// return FilesMock;
					return data
				}),
				catchError(error => {
					return this.handleError(error, "getFiles")
				})
			)
	}

	public getPdf(riferimento): Observable<Blob> {
		const headers = {
			"Content-Type": "application/json",
			Authorization: "Bearer " + this.accessToken
			// 'responseType': 'blob'
		}
		const uri = environment.DOMAIN_URL + "/api2/marketing/p2p/pdf/" + riferimento
		return this.http.get(uri, { headers, responseType: "blob" })
	}

	public getHTTPHeader(isAuth?: boolean) {
		if (!isAuth) {
			return {
				headers: new HttpHeaders({
					"Content-Type": "application/json",
					Accept: "application/ld+json"
				})
			}
		} else {
			return {
				headers: new HttpHeaders({
					Authorization: "Bearer " + this.accessToken
				})
			}
		}
	}

	public handleError(error: Response, operation: string = null) {
		switch (error.status) {
			case 400:
				break
			case 401:
				this.logout()
				break
			case 500:
				break
		}
		return throwError(error)
	}

	// Auth

	get isLogged(): boolean {
		return Number(localStorage.getItem(this.storageIsLogged)) === 1 ? true : false
	}

	public logout() {
		this.clear()
		window.location.replace(`${environment.DOMAIN_URL}/api2/saml/logout?redirect_uri=${environment.redirectSSOUrl}`)
	}

	// Toastr
	public showSuccess(text, options?) {
		text = text ? text : "Operation completed successfully"
		this.toastr.success(text, "", options ? options : this.toastrOptions)
	}

	public showError(e) {
		const errorMsg =
			e.error && e.error.error && e.error.error.message ? e.error.error.message : "Invalid credentials"
		this.toastr.error(errorMsg, "", this.toastrOptions)
	}

	public showErrorMsg(msg) {
		this.toastr.error(msg, "", this.toastrOptions)
	}

	// Translate

	public instant(key, params = null) {
		return this.translate.instant(key, params)
	}

	get lang(): string {
		return localStorage.getItem(this.storageLanguage)
	}

	// Notifications

	public setPolling() {
		setInterval(() => {
			if (this.polling$ instanceof Subscription) {
				this.polling$.unsubscribe()
			}
			this.polling$ = this.checkForNotifications().subscribe()
		}, 30000)
	}

	public checkForNotifications(): Observable<any> {
		return this.http
			.get<any>(environment.DOMAIN_URL + "/api2/marketing/notification/my/new/count", this.getHTTPHeader(true))
			.pipe(
				map(data => {
					return this.notifications.emit(data)
				}),
				catchError(error => {
					return this.handleError(error, "getFiles")
				})
			)
	}

	// Functions

	public open(content) {
		$("#" + content).modal("show")
	}

	public close(content) {
		$("#" + content).modal("hide")
	}

	public getExcelTemplate(urlXslx): Observable<Blob> {
		const uri = environment.DOMAIN_URL + urlXslx
		return this.http.get(uri, { responseType: "blob" })
	}

	public capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1)
	}

	public downloadPdf(riferimento): void {
		this.getPdf(riferimento).subscribe(x => {
			const newBlob = new Blob([x], { type: "application/pdf" })
			if (window.navigator && window.navigator.msSaveOrOpenBlob) {
				window.navigator.msSaveOrOpenBlob(newBlob)
				return
			}
			const data = window.URL.createObjectURL(newBlob)
			const link = document.createElement("a")
			link.href = data
			link.download = riferimento + ".pdf"
			link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }))
			setTimeout(function () {
				window.URL.revokeObjectURL(data)
				link.remove()
			}, 100)
		})
	}
}
